import { ArrowUpDown, Check } from "lucide-react";
import { InputV3 } from "components/common/input/InputV3";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
} from "components/common/DropdownV3";
import { cn } from "utils/cn";
import { ProjectsSearchAndFiltersProps, ProjectsSort } from "./ProjectsSearchAndFilters.types";

const SORT_OPTIONS: Array<{ id: ProjectsSort; label: string; triggerLabel: string }> = [
  { id: "newest", label: "Newest first", triggerLabel: "Newest" },
  { id: "oldest", label: "Oldest first", triggerLabel: "Oldest" },
  { id: "name", label: "Name A to Z", triggerLabel: "Name" },
];

export const ProjectsSearchAndFilters = ({
  title,
  count,
  showSearch,
  searchTerm,
  onSearchChange,
  sortBy,
  onSortChange,
}: ProjectsSearchAndFiltersProps) => {
  const activeSort = SORT_OPTIONS.find((option) => option.id === sortBy) ?? SORT_OPTIONS[0];

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h2 className="flex items-baseline gap-2 text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
        {title}
        <span className="tabular-nums text-muted-foreground/60">{count}</span>
      </h2>

      <div className="flex flex-1 items-center justify-end gap-2">
        {showSearch && (
          <div className="flex-1 sm:w-64 sm:flex-none">
            <InputV3
              type="search"
              placeholder="Search productions..."
              aria-label="Search productions"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="h-9 border-border bg-muted/40"
            />
          </div>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Sort projects: ${activeSort.label}`}
              className={cn(
                "flex h-9 shrink-0 items-center gap-2 rounded-lg px-2.5 text-sm text-muted-foreground transition-colors duration-200",
                "hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              )}
            >
              <ArrowUpDown className="h-4 w-4" />
              {activeSort.triggerLabel}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuLabel>Sort by</DropdownMenuLabel>
            <DropdownMenuGroup>
              {SORT_OPTIONS.map((option) => {
                const isSelected = option.id === sortBy;
                return (
                  <DropdownMenuItem
                    key={option.id}
                    aria-checked={isSelected}
                    role="menuitemradio"
                    onClick={() => onSortChange(option.id)}
                    className={cn(isSelected && "bg-muted")}
                  >
                    <span className="flex-1">{option.label}</span>
                    {isSelected && <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden />}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
